import { ReactRenderer } from '@tiptap/react'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import type { Editor } from '@tiptap/core'
import { TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state'
import MentionList from './MentionList'
import { actionTokenAllowsUserMentions } from './actionTokenMentionPolicy'
import { MENTION_SUGGESTION_POPUP_ATTR } from './mentionPluginKey'
import { normalizeSuggestionRange } from './mentionSuggestionRange'
import { createMentionUserSource, type MentionUsersRefreshResult } from './mentionUserSource'
import { rangeIsInsideCodeLiteral } from './codeLiteralContext'

const pendingExactMentionCommitters = new WeakMap<Editor, () => boolean>()

// Turns an exactly typed "@name" at the caret into a user mention, as pressing
// space would. Returns whether a mention was inserted.
export const commitPendingExactMention = (editor: Editor | null | undefined): boolean =>
    !!editor && (pendingExactMentionCommitters.get(editor)?.() ?? false)

// Helper function for fuzzy matching
const fuzzyMatch = (text: string, query: string): boolean => {
    // First check for simple inclusion
    if (text.includes(query)) return true

    // Check if all query characters appear in order in the text
    let queryIndex = 0
    for (let i = 0; i < text.length && queryIndex < query.length; i++) {
        if (text[i] === query[queryIndex]) {
            queryIndex++
        }
    }
    return queryIndex === query.length
}

// Mention item shape passed around by the suggestion system
interface MentionSuggestionItem {
    id: string
    content?: string
    name?: string
    value?: string
    meta?: string
    kind?: 'image' | 'video' | 'audio'
    previewUrl?: string | null
    url?: string | null
    contentType?: string | null
    link?: string
    type?: string
    denotationChar?: string
    triggerType?: 'user' | 'model'
    searchableName?: string
    searchName?: string
    isModel?: boolean
    [key: string]: unknown
}

const buildUserSuggestionItems = (
    users: MentionSuggestionItem[],
    query: string,
): MentionSuggestionItem[] => {
    const referenceUsers = users.filter(user => user.type === 'reference')
    const isReferenceOnlySuggestion =
        referenceUsers.length > 0 && referenceUsers.length === users.length

    const filteredUsers = users
        .filter(user => {
            const userName = user.content?.startsWith('@')
                ? user.content.substring(1).toLowerCase()
                : user.content?.toLowerCase() || ''
            const queryLower = query.toLowerCase()
            const referenceMeta = (user.meta || '').toLowerCase()

            return (
                fuzzyMatch(userName, queryLower) ||
                (user.type === 'reference' && fuzzyMatch(referenceMeta, queryLower))
            )
        })
        .sort((a, b) => {
            const queryLower = query.toLowerCase()
            const aUserName = a.content?.startsWith('@')
                ? a.content.substring(1).toLowerCase()
                : a.content?.toLowerCase() || ''
            const bUserName = b.content?.startsWith('@')
                ? b.content.substring(1).toLowerCase()
                : b.content?.toLowerCase() || ''
            const aExact = aUserName === queryLower
            const bExact = bUserName === queryLower
            const aStarts = aUserName.startsWith(queryLower)
            const bStarts = bUserName.startsWith(queryLower)
            const aContains = aUserName.includes(queryLower)
            const bContains = bUserName.includes(queryLower)

            if (aExact && !bExact) return -1
            if (!aExact && bExact) return 1
            if (aStarts && !bStarts) return -1
            if (!aStarts && bStarts) return 1
            if (aContains && !bContains) return -1
            if (!aContains && bContains) return 1
            if (!aContains && !bContains) return aUserName.length - bUserName.length

            return 0
        })
        .slice(0, 10)

    const fallbackReferenceUsers =
        isReferenceOnlySuggestion && filteredUsers.length === 0
            ? referenceUsers.slice(0, 10)
            : filteredUsers

    return fallbackReferenceUsers.map(user => ({
        ...user,
        type: user.type === 'reference' ? 'reference' : 'user',
        denotationChar: '@',
        triggerType: 'user',
    }))
}

const isWhitespace = (text: string) => /^[\s\u00A0]$/.test(text)

const textAt = (tr: Transaction, position: number) => {
    if (position < 0 || position >= tr.doc.content.size) return ''
    return tr.doc.textBetween(position, position + 1, '', '')
}

const ensureSpaceAtPosition = (tr: Transaction, state: EditorState, position: number) => {
    const nextChar = textAt(tr, position)
    const previousChar = textAt(tr, position - 1)

    let cursorPos = position

    if (isWhitespace(nextChar)) {
        cursorPos = position + 1
    } else if (isWhitespace(previousChar)) {
        cursorPos = position
    } else {
        tr.insert(position, state.schema.text(' '))
        cursorPos = position + 1
    }

    tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(cursorPos, tr.doc.content.size))))
}

const ensureTrailingSpaceAtPosition = (tr: Transaction, state: EditorState, position: number) => {
    const nextChar = textAt(tr, position)
    const cursorPos = position + 1

    if (!isWhitespace(nextChar)) {
        tr.insert(position, state.schema.text(' '))
    }

    tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(cursorPos, tr.doc.content.size))))
}

const actionTokenAtRange = (state: EditorState, range?: { from: number; to: number } | null) => {
    const from = range?.from ?? state.selection.from
    const to = range?.to ?? state.selection.to
    let match: { node: EditorState['doc']; pos: number } | null = null

    state.doc.descendants((node, pos) => {
        if (node.type.name !== 'actionTag') return true

        const contentFrom = pos + 1
        const contentTo = pos + node.nodeSize - 1
        if (from >= contentFrom && to <= contentTo) {
            match = { node: node as EditorState['doc'], pos }
        }
        return false
    })

    return match
}

const createMentionNode = (state: EditorState, props: MentionSuggestionItem) => {
    return state.schema.nodes.mention.create({
        id: props.id,
        label: props.content,
        type: props.type,
        denotationChar: props.denotationChar,
        content: props.content,
        link: props.link,
        kind: props.kind,
        previewUrl: props.previewUrl,
        url: props.url,
        contentType: props.contentType,
    })
}

const getMentionPopupAppendTarget = (editor: Editor) => {
    const editorElement = editor.view.dom as HTMLElement | null
    const knovModalSurface = editorElement?.closest<HTMLElement>('.knov-modal-comp')
    const modalElement = editorElement?.closest<HTMLElement>(
        '[aria-modal="true"], [role="dialog"], dialog',
    )
    const overlayElement =
        modalElement?.parentElement && modalElement.parentElement !== document.body
            ? modalElement.parentElement
            : null

    return knovModalSurface || overlayElement || modalElement || document.body
}

const insertMentionWithTrailingSpace = (
    tr: Transaction,
    state: EditorState,
    position: number,
    props: MentionSuggestionItem,
) => {
    const mentionNode = createMentionNode(state, props)

    tr.insert(position, mentionNode)
    ensureTrailingSpaceAtPosition(tr, state, position + mentionNode.nodeSize)

    return mentionNode
}

// Define the suggestion configuration for both @ and @@ triggers
export const createMentionSuggestion = (
    getUsersFn: () => MentionSuggestionItem[],
    getModelsFn: () => MentionSuggestionItem[],
    setAgentModel?: (model: string | null) => void,
    getActionTokenReferencesFn?: (attrs: Record<string, unknown>) => MentionSuggestionItem[],
    refreshUsersFn?: () => MentionUsersRefreshResult<MentionSuggestionItem>,
) => {
    let updateRenderedUserItems:
        | ((users: MentionSuggestionItem[], query: string) => void)
        | undefined
    let lastUserQuery: string | null = null
    const getMentionUsers = createMentionUserSource(getUsersFn, refreshUsersFn, refreshedUsers => {
        if (lastUserQuery !== null) {
            updateRenderedUserItems?.(refreshedUsers, lastUserQuery)
        }
    })

    return {
        items: ({ query, editor }: { query: string; editor: Editor }) => {
            const { state } = editor
            const actionTokenMatch = actionTokenAtRange(state)
            const actionTokenReferenceItems = actionTokenMatch
                ? getActionTokenReferencesFn?.(
                      (actionTokenMatch.node.attrs || {}) as Record<string, unknown>,
                  ) || []
                : []
            const allowActionTokenUserMentions = actionTokenAllowsUserMentions(
                actionTokenMatch?.node.attrs as Record<string, unknown> | undefined,
            )
            // Action tags are text-only, so allowed token suggestions insert plain @labels.
            // Most tokens expose only media references; pay also exposes user recipients.
            const models = actionTokenMatch ? [] : getModelsFn()

            const { selection } = state
            const { $from } = selection

            // Get more context to better detect triggers
            const textBefore = state.doc.textBetween(Math.max(0, $from.pos - 30), $from.pos, '', '')
            //console.log(
            //    '[MentionSuggestion] Full textBefore:',
            //    `"${textBefore}"`,
            //    'query:',
            //    `"${query}"`,
            //)

            // More precise pattern matching for @@ vs @
            // Look for @@ pattern at word boundaries or start/space
            // Include hyphens, underscores, and dots in username patterns
            const doubleAtPattern = /(?:^|[\s\n])@@([a-zA-Z0-9\-_.]*)$/
            const singleAtPattern = /(?:^|[\s\n])@([a-zA-Z0-9\-_.]*)$/

            const doubleAtMatch = textBefore.match(doubleAtPattern)
            const singleAtMatch = textBefore.match(singleAtPattern)

            // Determine trigger type based on matches
            let triggerType = null
            let actualQuery = query

            // Special case: if query starts with @, it means we're transitioning from @ to @@
            if (query.startsWith('@')) {
                triggerType = 'model'
                actualQuery = query.substring(1) // Remove the @ from query
                //console.log(
                //    '[MentionSuggestion] Query-based model trigger detected (@ → @@), query:',
                //    actualQuery,
                //)
            } else if (doubleAtMatch && !textBefore.includes('@@@')) {
                // Model trigger @@
                triggerType = 'model'
                actualQuery = doubleAtMatch[1] || query
                //console.log(
                //    '[MentionSuggestion] Pattern-based model trigger detected, query:',
                //    actualQuery,
                //)
            } else if (singleAtMatch && !doubleAtMatch && !query.startsWith('@')) {
                // User trigger @ (but not part of @@, and query doesn't start with @)
                triggerType = 'user'
                actualQuery = singleAtMatch[1] || query
                //console.log('[MentionSuggestion] User trigger detected, query:', actualQuery)
            }

            // Alternative detection: check the immediate characters before cursor
            if (!triggerType) {
                const lastChars = textBefore.slice(-10)
                if (lastChars.includes('@@')) {
                    const afterDoubleAt = lastChars.split('@@').pop() || ''
                    if (afterDoubleAt === query || afterDoubleAt.includes(query)) {
                        triggerType = 'model'
                        actualQuery = afterDoubleAt
                        //console.log(
                        //    '[MentionSuggestion] Alternative model detection, query:',
                        //    actualQuery,
                        //)
                    }
                } else if (lastChars.includes('@') && !lastChars.includes('@@')) {
                    const afterAt = lastChars.split('@').pop() || ''
                    if (afterAt === query || afterAt.includes(query)) {
                        triggerType = 'user'
                        actualQuery = afterAt
                        //console.log(
                        //    '[MentionSuggestion] Alternative user detection, query:',
                        //    actualQuery,
                        //)
                    }
                }
            }

            if (triggerType === 'model') {
                lastUserQuery = null
                if (actionTokenMatch) return []

                //console.log(
                //    '[MentionSuggestion] Processing models:',
                //    models.length,
                //    'models available',
                //)
                //console.log('[MentionSuggestion] Sample model:', models[0])
                //console.log('[MentionSuggestion] Query for models:', `"${actualQuery}"`)

                // If no query, show all models
                if (!actualQuery || actualQuery.trim() === '') {
                    const allModels = models.slice(0, 10).map(model => ({
                        ...model,
                        type: 'model',
                        denotationChar: '@@',
                        triggerType: 'model',
                    }))

                    //console.log(
                    //    '[MentionSuggestion] Returning all models (no query):',
                    //    allModels.length,
                    //)
                    return allModels
                }

                // Filter models with flexible property matching
                const filteredModels = models
                    .filter(model => {
                        // Try multiple possible property names for model content
                        const content = (
                            model.content ||
                            model.name ||
                            model.id ||
                            model.value ||
                            ''
                        ).toLowerCase()
                        const searchName = (
                            model.searchableName ||
                            model.searchName ||
                            ''
                        ).toLowerCase()
                        const queryLower = actualQuery.toLowerCase()

                        const matches =
                            fuzzyMatch(content, queryLower) || fuzzyMatch(searchName, queryLower)
                        //console.log('[MentionSuggestion] Model filter:', {
                        //    model: model.content || model.name || model.id,
                        //    content,
                        //    searchName,
                        //    queryLower,
                        //    matches,
                        //})

                        return matches
                    })
                    .sort((a, b) => {
                        // Sort to prioritize exact matches
                        const queryLower = actualQuery.toLowerCase()

                        // Get content for comparison
                        const aContent = (
                            a.content ||
                            a.name ||
                            a.id ||
                            a.value ||
                            ''
                        ).toLowerCase()
                        const bContent = (
                            b.content ||
                            b.name ||
                            b.id ||
                            b.value ||
                            ''
                        ).toLowerCase()

                        // Check for exact matches
                        const aExact = aContent === queryLower
                        const bExact = bContent === queryLower

                        // Check if starts with query
                        const aStarts = aContent.startsWith(queryLower)
                        const bStarts = bContent.startsWith(queryLower)

                        // Check if contains as substring
                        const aContains = aContent.includes(queryLower)
                        const bContains = bContent.includes(queryLower)

                        // Sort order: exact match > starts with > contains > fuzzy match
                        if (aExact && !bExact) return -1
                        if (!aExact && bExact) return 1
                        if (aStarts && !bStarts) return -1
                        if (!aStarts && bStarts) return 1
                        if (aContains && !bContains) return -1
                        if (!aContains && bContains) return 1

                        // If both are fuzzy matches, prefer shorter strings (more likely to be relevant)
                        if (!aContains && !bContains) {
                            return aContent.length - bContent.length
                        }

                        // If both are similar, maintain original order
                        return 0
                    })
                    .slice(0, 10)
                    .map(model => ({
                        ...model,
                        type: 'model',
                        denotationChar: '@@',
                        triggerType: 'model',
                    }))

                //console.log('[MentionSuggestion] Returning filtered models:', filteredModels.length)
                return filteredModels
            } else if (triggerType === 'user') {
                //console.log(
                //    '[MentionSuggestion] Processing users:',
                //    users.length,
                //    'users available',
                //)
                //console.log('[MentionSuggestion] Sample user:', users[0])
                //console.log('[MentionSuggestion] Query for users:', `"${actualQuery}"`)

                const users = actionTokenMatch
                    ? allowActionTokenUserMentions
                        ? getMentionUsers()
                        : actionTokenReferenceItems
                    : getMentionUsers()

                lastUserQuery = actualQuery
                return buildUserSuggestionItems(users, actualQuery)
            }

            //console.log(
            //    '[MentionSuggestion] No trigger detected - textBefore:',
            //    `"${textBefore}"`,
            //    'query:',
            //    `"${query}"`,
            //)
            //console.log('[MentionSuggestion] Available data:', {
            //    users: users?.length,
            //    models: models?.length,
            //})
            lastUserQuery = null
            return []
        },

        render: () => {
            let component: ReactRenderer | undefined
            let popup: TippyInstance[] | undefined
            // Track latest items/editor for key handling
            let lastItems: MentionSuggestionItem[] = []
            let lastEditor: Editor | undefined
            let lastProps: SuggestionProps<MentionSuggestionItem> | undefined

            const trySelectExactMatch = ({
                editorInstance,
                suggestionItems,
                allowTrailingSpace = false,
            }: {
                editorInstance: Editor
                suggestionItems: MentionSuggestionItem[]
                allowTrailingSpace?: boolean
            }) => {
                if (!editorInstance || !suggestionItems?.length) return null

                const { state } = editorInstance
                const { selection } = state
                const { $from } = selection

                const rawTextBefore = state.doc.textBetween(
                    Math.max(0, $from.pos - 30),
                    $from.pos,
                    '',
                    '',
                )

                const whitespaceMatch =
                    allowTrailingSpace && rawTextBefore ? rawTextBefore.match(/[ \u00A0]+$/) : null
                const trailingWhitespace = whitespaceMatch ? whitespaceMatch[0].length : 0
                const textBefore = allowTrailingSpace
                    ? rawTextBefore.slice(0, rawTextBefore.length - trailingWhitespace)
                    : rawTextBefore

                const doubleAtPattern = /(?:^|[\s\n])@@([a-zA-Z0-9\-_.]*)$/
                const singleAtPattern = /(?:^|[\s\n])@([a-zA-Z0-9\-_.]*)$/

                const doubleAtMatch = textBefore.match(doubleAtPattern)
                const singleAtMatch = textBefore.match(singleAtPattern)

                let triggerType: 'model' | 'user' | null = null
                let actualQuery = ''

                if (doubleAtMatch && !textBefore.includes('@@@')) {
                    triggerType = 'model'
                    actualQuery = doubleAtMatch[1] || ''
                } else if (singleAtMatch && !doubleAtMatch) {
                    triggerType = 'user'
                    actualQuery = singleAtMatch[1] || ''
                }

                if (!triggerType || !actualQuery) return null

                const queryLower = actualQuery.toLowerCase()

                const exactItem = suggestionItems.find((item: MentionSuggestionItem) => {
                    if (triggerType === 'model' || item.triggerType === 'model') {
                        return (item.content || '').toLowerCase() === queryLower
                    }
                    const username = (item.content || '').startsWith('@')
                        ? (item.content || '').slice(1)
                        : item.content || ''
                    return username.toLowerCase() === queryLower
                })

                if (!exactItem) return null

                return {
                    exactItem,
                    triggerType,
                    trailingWhitespace,
                }
            }

            // Submitting straight after typing "@name" never reaches the space-key
            // exact-match select, so composers commit it before reading content.
            const commitExactMatch = () => {
                if (!lastEditor || !lastProps?.command) return false
                const result = trySelectExactMatch({
                    editorInstance: lastEditor,
                    suggestionItems: lastItems,
                })
                // Only people: committing an @@model would invoke the agent.
                if (result?.triggerType !== 'user') return false
                lastProps.command(result.exactItem)
                return true
            }

            // ProseMirror preserves Android's live composition DOM without rendering its
            // suggestion decoration. Anchor Tippy to the native selection until that
            // decoration (and its normal clientRect) exists.
            const getAndroidCompositionClientRect = (
                props: SuggestionProps<MentionSuggestionItem>,
            ) => {
                const view = props.editor.view
                if (
                    typeof navigator === 'undefined' ||
                    !/\bAndroid\b/i.test(navigator.userAgent) ||
                    !view.composing
                ) {
                    return null
                }

                return () => {
                    const selection = view.dom.ownerDocument.getSelection()
                    if (selection?.rangeCount) {
                        const range = selection.getRangeAt(0)
                        if (view.dom.contains(range.commonAncestorContainer)) {
                            const rect = range.getBoundingClientRect()
                            if (rect.width || rect.height) return rect

                            const clientRects = range.getClientRects()
                            if (clientRects.length) return clientRects[clientRects.length - 1]
                        }
                    }

                    return view.dom.getBoundingClientRect()
                }
            }

            const mountOrUpdatePopup = (props: SuggestionProps<MentionSuggestionItem>) => {
                const clientRect = props.clientRect || getAndroidCompositionClientRect(props)
                if (!clientRect || !component) return

                if (popup?.[0]) {
                    popup[0].setProps({
                        getReferenceClientRect: clientRect,
                    })
                    return
                }

                popup = tippy('body', {
                    getReferenceClientRect: clientRect,
                    appendTo: () => getMentionPopupAppendTarget(props.editor),
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    zIndex: 1000002,
                })
            }

            updateRenderedUserItems = (users, query) => {
                if (!component || !lastProps) return

                const items = buildUserSuggestionItems(users, query)
                lastItems = items
                lastProps = { ...lastProps, items }
                component.updateProps(lastProps)
            }

            return {
                onStart: (props: SuggestionProps<MentionSuggestionItem>) => {
                    //console.log('[MentionSuggestion] onStart:', props)
                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    })
                    component.element.setAttribute(
                        'data-suggestion-editor',
                        props.editor.instanceId,
                    )
                    component.element.setAttribute(MENTION_SUGGESTION_POPUP_ATTR, 'true')
                    component.element.setAttribute('data-testid', 'mention-suggestion-popup')

                    // Keep current items/editor cached for onKeyDown logic
                    lastItems = props.items || []
                    lastEditor = props.editor
                    lastProps = props
                    pendingExactMentionCommitters.set(props.editor, commitExactMatch)

                    mountOrUpdatePopup(props)
                },

                onUpdate(props: SuggestionProps<MentionSuggestionItem>) {
                    //console.log('[MentionSuggestion] onUpdate:', props)
                    component?.updateProps(props)

                    // Update cached items/editor
                    lastItems = props.items || []
                    lastEditor = props.editor || lastEditor
                    lastProps = props

                    mountOrUpdatePopup(props)
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                    const event = props.event as KeyboardEvent

                    if (event.key === 'Escape') {
                        popup?.[0]?.hide()
                        return true
                    }

                    // Auto-select exact match on space for both @ and @@ mentions
                    const keyEvent = event as KeyboardEvent & {
                        keyCode?: number
                        which?: number
                        charCode?: number
                    }
                    // Mobile keyboards often report space as "Unidentified", so fall back to code/keyCode.
                    const normalizedKey = keyEvent.key?.toLowerCase()
                    const code = keyEvent.code
                    const keyCode = keyEvent.keyCode ?? keyEvent.which ?? keyEvent.charCode ?? 0

                    const isSpace =
                        normalizedKey === ' ' ||
                        normalizedKey === 'space' ||
                        normalizedKey === 'spacebar' ||
                        code === 'Space' ||
                        keyCode === 32
                    if (isSpace) {
                        try {
                            const editor =
                                lastEditor ??
                                (component as ReactRenderer & { props?: { editor?: Editor } })
                                    ?.props?.editor
                            const componentProps = (
                                component as ReactRenderer & {
                                    props?: {
                                        items?: MentionSuggestionItem[]
                                        command?: (item: MentionSuggestionItem) => void
                                    }
                                }
                            )?.props
                            const items = lastItems || componentProps?.items || []

                            if (editor && items?.length) {
                                const result = trySelectExactMatch({
                                    editorInstance: editor,
                                    suggestionItems: items,
                                })

                                if (result) {
                                    const listProps = componentProps
                                    if (listProps?.command) {
                                        // Prevent default space; command will insert mention + space
                                        event.preventDefault()
                                        listProps.command(result.exactItem)
                                        return true
                                    }
                                }
                            }
                        } catch (e) {
                            // Swallow and fall through to default handling
                        }
                    }

                    const isAndroidImeSpace =
                        keyCode === 229 ||
                        normalizedKey === 'process' ||
                        normalizedKey === 'unidentified' ||
                        code === 'Unidentified'

                    if (isAndroidImeSpace) {
                        const editor =
                            lastEditor ??
                            (component as ReactRenderer & { props?: { editor?: Editor } })?.props
                                ?.editor
                        const androidComponentProps = (
                            component as ReactRenderer & {
                                props?: {
                                    items?: MentionSuggestionItem[]
                                    command?: (item: MentionSuggestionItem) => void
                                }
                            }
                        )?.props
                        const items = lastItems || androidComponentProps?.items || []
                        const command = androidComponentProps?.command

                        if (editor && items?.length && command) {
                            const itemsSnapshot = [...items]
                            window.setTimeout(() => {
                                try {
                                    const imeResult = trySelectExactMatch({
                                        editorInstance: editor,
                                        suggestionItems: itemsSnapshot,
                                        allowTrailingSpace: true,
                                    })

                                    if (imeResult?.exactItem) {
                                        const trailingSpaceCount = imeResult.trailingWhitespace || 0
                                        if (trailingSpaceCount > 0) {
                                            const currentState = editor.state
                                            const deleteFrom = Math.max(
                                                0,
                                                currentState.selection.from - trailingSpaceCount,
                                            )
                                            const deleteTo = currentState.selection.from
                                            if (deleteTo > deleteFrom) {
                                                editor.commands.deleteRange({
                                                    from: deleteFrom,
                                                    to: deleteTo,
                                                })
                                            }
                                        }
                                        command(imeResult.exactItem)
                                    }
                                } catch (err) {
                                    // ignore IME fallback failures
                                }
                            }, 0)
                        }
                    }

                    return (
                        (
                            component?.ref as {
                                onKeyDown?: (props: SuggestionKeyDownProps) => boolean
                            }
                        )?.onKeyDown?.(props) || false
                    )
                },

                onExit() {
                    //console.log('[MentionSuggestion] onExit')
                    if (lastEditor) pendingExactMentionCommitters.delete(lastEditor)
                    lastProps = undefined
                    popup?.[0]?.destroy()
                    popup = undefined
                    component?.destroy()
                    component = undefined
                    lastItems = []
                    lastEditor = undefined
                },
            }
        },

        // Handle both @ and @@ triggers
        char: '@',

        // Allow spaces in suggestions
        allowSpaces: true,

        // Custom character detection to handle @@
        allowedPrefixes: [' ', '\n', null],

        startOfLine: false,

        allow: ({ state, range }: { state: EditorState; range: { from: number; to: number } }) => {
            if (rangeIsInsideCodeLiteral(state, range)) return false

            const actionTokenMatch = actionTokenAtRange(state, range)
            if (!actionTokenMatch) return true
            if (
                actionTokenAllowsUserMentions(
                    actionTokenMatch.node.attrs as Record<string, unknown>,
                )
            ) {
                return true
            }

            return (
                (
                    getActionTokenReferencesFn?.(
                        (actionTokenMatch.node.attrs || {}) as Record<string, unknown>,
                    ) || []
                ).length > 0
            )
        },

        // Custom command to handle insertion
        command: ({
            editor,
            range,
            props,
        }: {
            editor: Editor
            range: { from: number; to: number }
            props: MentionSuggestionItem
        }) => {
            //console.log('[MentionSuggestion] Command called:', { range, props })
            const actionTokenMatch = actionTokenAtRange(editor.state, range)
            const insideActionToken = !!actionTokenMatch
            const allowActionTokenUserMention =
                actionTokenAllowsUserMentions(
                    actionTokenMatch?.node.attrs as Record<string, unknown> | undefined,
                ) && props.type === 'user'
            if (insideActionToken && props.type !== 'reference' && !allowActionTokenUserMention) {
                return
            }

            const { triggerType } = props

            // For model mentions, we need special handling
            if (triggerType === 'model') {
                // Use a custom command that handles everything in one transaction
                const success = editor
                    .chain()
                    .focus()
                    .command(({ tr, state, dispatch }) => {
                        const { doc } = state
                        const mentionRange = normalizeSuggestionRange(state, range, 'model')

                        // Find existing model mentions
                        const existingMentions: Array<{ pos: number; id: string; size: number }> =
                            []
                        doc.descendants((node, pos) => {
                            if (
                                node.type.name === 'mention' &&
                                node.attrs?.denotationChar === '@@'
                            ) {
                                existingMentions.push({
                                    pos,
                                    id: node.attrs.id,
                                    size: node.nodeSize,
                                })
                            }
                        })

                        //console.log(
                        //    '[MentionSuggestion] Found existing mentions:',
                        //    existingMentions,
                        //)

                        // Check if it's the same model
                        if (existingMentions.some(mention => mention.id === props.id)) {
                            //console.log(
                            //    '[MentionSuggestion] Same model exists, just removing trigger',
                            //)
                            tr.delete(mentionRange.from, mentionRange.to)
                            const insertionPos = tr.mapping.map(mentionRange.from, -1)
                            ensureSpaceAtPosition(tr, state, insertionPos)
                            if (dispatch) dispatch(tr)
                            return true
                        }

                        // Start with deleting the trigger text
                        tr.delete(mentionRange.from, mentionRange.to)

                        // Remove existing model mentions (adjust positions after trigger deletion)
                        existingMentions.forEach(mention => {
                            const adjustedPos = tr.mapping.map(mention.pos, -1)

                            // Check if node still exists at adjusted position
                            const nodeAtPos = tr.doc.nodeAt(adjustedPos)
                            if (nodeAtPos && nodeAtPos.type.name === 'mention') {
                                //console.log(
                                //    '[MentionSuggestion] Removing mention at adjusted pos:',
                                //    adjustedPos,
                                //)
                                tr.delete(adjustedPos, adjustedPos + mention.size)
                            }
                        })

                        const insertionPos = tr.mapping.map(mentionRange.from, -1)
                        insertMentionWithTrailingSpace(tr, state, insertionPos, props)

                        // Dispatch the transaction
                        if (dispatch) {
                            dispatch(tr)
                        }

                        return true
                    })
                    .run()

                // Update the agent model state
                if (success && setAgentModel && props.id) {
                    //console.log('[MentionSuggestion] Setting agent model:', props.id)
                    setAgentModel(props.id)
                }
            } else if (
                insideActionToken &&
                (props.type === 'reference' || allowActionTokenUserMention)
            ) {
                editor
                    .chain()
                    .focus()
                    .command(({ tr, state, dispatch }) => {
                        const mentionRange = normalizeSuggestionRange(state, range, 'user')
                        const label = String(props.content || props.value || '')
                            .replace(/^@+/, '')
                            .trim()
                        if (!label) return true

                        tr.delete(mentionRange.from, mentionRange.to)
                        const insertionPos = tr.mapping.map(mentionRange.from, -1)
                        const mentionText = `@${label} `
                        tr.insertText(mentionText, insertionPos)
                        tr.setSelection(
                            TextSelection.create(tr.doc, insertionPos + mentionText.length),
                        )

                        if (dispatch) dispatch(tr)
                        return true
                    })
                    .run()
            } else {
                // Insert the user mention and its trailing space in one transaction.
                editor
                    .chain()
                    .focus()
                    .command(({ tr, state, dispatch }) => {
                        const mentionRange = normalizeSuggestionRange(state, range, 'user')

                        tr.delete(mentionRange.from, mentionRange.to)
                        const insertionPos = tr.mapping.map(mentionRange.from, -1)
                        insertMentionWithTrailingSpace(tr, state, insertionPos, props)

                        if (dispatch) dispatch(tr)
                        return true
                    })
                    .run()
            }
        },
    }
}
